<template>
  <div
    v-on-clickaway="close"
    :class="classes"
  >
    <div
      class="select__field-wrapper"
      :class="{ disabled: disabled }"
      @click="onActivate"
    >
      <label
        v-if="label"
        :for="id"
        class="select__label"
      >
        {{ label }}
        <span v-if="required" class="select__label-required">*</span>
      </label>

      <div class="select__field">
        <div class="select__text">
          {{ text }}
        </div>
        <ChevronIcon class="select__arrow" />
      </div>
    </div>

    <div :class="['select__dropdown', `select__dropdown_scrollbar_${scrollbar}`]">
      <div
        v-for="(item, index) in filteredItems"
        :key="`select-item-${index}-in-${id}`"
        class="select__dropdown-item"
        @click="onItemSelect(item)"
      >
        {{ item[itemTextKey] }}
      </div>
    </div>

    <transition name="slide-from-top">
      <span
        v-if="error && errorMsg"
        class="select__error-msg"
      >
        {{ errorMsg }}
      </span>
    </transition>
  </div>
</template>

<script>
  import ChevronIcon from '@/assets/img/icons/chevron.svg?inline'
  import { mixin as clickaway } from 'vue-clickaway'
  import { get } from 'lodash'

  export default {
    name: 'BaseSelect',
    components: {
      ChevronIcon
    },
    mixins: [ clickaway ],
    props: {
      emitOnCreate: {
        type: Boolean,
        default: true
      },

      value: {
        type: null,
        default: ''
      },
      disabled: {
        type: [Boolean, Number, String],
        default: false
      },
      required: {
        type: Boolean,
        default: false
      },
      label: {
        type: String,
        default: ''
      },
      placeholder: {
        type: String,
        default: ''
      },
      items: {
        type: Array,
        requied: true
      },
      itemValueKey: {
        type: String,
        default: 'value'
      },
      itemTextKey: {
        type: String,
        default: 'text'
      },
      errorMsg: {
        type: String,
        default: ''
      },
      scrollbar: {
        type: String,
        default: 'custom'
      },
      disable: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isActive: false,
        error: null
      }
    },
    computed: {
      selected () {
        return !this.value ? null : this.items.filter(({ value }) => value === this.value)[0]
      },
      id () {
        return `select-${this._uid}`
      },
      text () {
        return get(this, ['selected', this.itemTextKey]) || this.placeholder
      },
      classes () {
        return [
          'select',
          {
            'select_active': this.isActive,
            'select_error': this.error,
            'select_no-items': this.items.length < 1 || this.disable
          }
        ]
      },
      filteredItems () {
        const filteredItems = this.items.filter(item => item.value !== get(this, 'selected.value'))
        if (this.items.length === 1 && get(this, 'selected.value')) {
          filteredItems.push({ text: this.placeholder })
        }
        return filteredItems
      }
    },
    watch: {
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },
    created () {
      if (!this.placeholder && this.emitOnCreate) {
        this.onItemSelect(this.items[0])
      }
    },
    methods: {
      onActivate () {
        this.isActive = !this.isActive
        this.error = false
      },
      onItemSelect (item) {
        const selected = item || {}
        this.$emit('input', selected[this.itemValueKey], selected)
        this.close()
      },
      close () {
        this.isActive = false
      }
    }
  }
</script>

<style lang="scss">
  $padding-for-error: 30px;
  $active-box-shadow: 2px 2px 5px rgba($c-black, 0.1);

  .select {
    position: relative;
    display: block;
    min-width: 290px;
    // max-width: 335px;
    padding-bottom: $padding-for-error;
    cursor: pointer;

    @include mobile {
      max-width: 100%;
    }

    &__label {
      display: inline-block;
      padding-bottom: 1em;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      color: #6a6b70;

      &-required {
        color: $c-active-light;
      }
    }

    &__text {
      padding-right: 10px;
      color: $c-gray-dark;
    }

    &__field-wrapper {
      &.disabled {
        pointer-events: none;
        opacity: 0.7;
      }
    }

    &__field {
      position: relative;
      display: block;
      width: 100%;
      min-height: 46px;
      padding: 0.938em 1.3em;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 15px;
      line-height: 1;
      background-color: $c-gray-extra-light-2;
      border-radius: 4px;
      outline: none;
      caret-color: $c-active-light;
      transition: 0.3s;

      &:focus {
        background-color: $c-gray;
      }

      @include hover {
        background-color: $c-gray;
      }
    }

    &__arrow {
      position: absolute;
      right: 4%;
      bottom: 35%;
      width: 13px;
      height: 13px;
      stroke: $c-active-light;
      transition: 0.3s;
      transform: rotate(-90deg);
    }

    &__dropdown {
      position: absolute;
      top: calc(100% - #{$padding-for-error});
      left: 0;
      z-index: 999;
      width: 100%;
      max-height: 270px;
      overflow-y: auto;
      visibility: hidden;
      border-bottom-right-radius: 4px;
      border-bottom-left-radius: 4px;
      box-shadow: $active-box-shadow;
      opacity: 0;
      transition: 0.3s;
      -webkit-overflow-scrolling: touch;

      &_scrollbar {
        &_custom {
          @include custom-scrollbar();
        }

        &_none {
          @include no-scrollbar();
        }
      }

      &-item {
        padding: 0.938em 1.3em;
        font-size: 15px;
        line-height: 1;
        color: #333;
        background-color: $c-gray-extra-light-2;
        border-top: 1px solid rgba(0, 0, 0, $alpha: 0.05);
        transition: background-color 0.3s;

        @include hover {
          background-color: $c-gray;
        }
      }
    }

    &__error-msg {
      position: absolute;
      bottom: 23px;
      left: 0;
      z-index: 1;
      height: 0;
      font-size: 12px;
      color: $c-active;
    }

    &_error {
      .select {
        &__field {
          background-color: $c-error-light;
        }
      }
    }

    &_no-items {
      pointer-events: none;
      opacity: 0.6;

      .select__arrow {
        opacity: 0;
      }
    }

    &_active {
      .select {
        &__field {
          background-color: $c-gray;
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 0;
          box-shadow: $active-box-shadow;
        }

        &__arrow {
          transform: rotate(-270deg);
        }

        &__dropdown {
          visibility: visible;
          opacity: 1;
        }
      }
    }
  }
</style>
